import { useCallback, useEffect, useRef, useState } from 'react';
import { PEOPLE_KEY, type BhStore, type MonitorSnapshot } from '@bhorosha/data';
import { REMINDER_TIMES_KEY, withFamilyTimes, type InjectionReminder, type Medicine, type Person } from '@bhorosha/core';
import { backend } from '../data';
import type { PersonData } from './model';

const EMPTY: MonitorSnapshot = { doses: [], bp: [], events: [], devices: [] };

function readJSON<T>(store: BhStore, key: string, fallback: T): T {
  try { const v = store.getItem(key); return v ? (JSON.parse(v) as T) : fallback; } catch { return fallback; }
}

/** People with their medicines and injection reminders, from the family's offline copy. */
export function readPeopleData(store: BhStore): PersonData[] {
  const people = readJSON<Person[]>(store, PEOPLE_KEY, []);
  const familyTimes = store.getItem(REMINDER_TIMES_KEY); // set by the family owner
  return people.map(person => ({
    person: withFamilyTimes(person, familyTimes),
    meds: readJSON<Medicine[]>(store, `mpc_medicines_v1::${person.id}`, []).map(m => ({ ...m, schedule_type: m.schedule_type || 'daily' })),
    reminders: readJSON<InjectionReminder[]>(store, `mpc_reminders_v1::${person.id}`, []),
  }));
}

/**
 * Live data for the Daily log / BP tab: re-loads when the phone (or anyone)
 * marks a dose, saves a reading or reports in, and every minute (so "later"
 * turns into "not marked yet" on time).
 */
export function useMonitor(store: BhStore, familyId: string, fromISO: string, toISO: string, active: boolean) {
  const [snap, setSnap] = useState<MonitorSnapshot>(EMPTY);
  const [people, setPeople] = useState<PersonData[]>(() => readPeopleData(store));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [now, setNow] = useState(() => new Date());
  const seq = useRef(0);

  const reload = useCallback(async () => {
    const my = ++seq.current;
    try {
      const s = await backend.loadMonitor(familyId, fromISO, toISO);
      if (my !== seq.current) return;
      setSnap(s); setError('');
    } catch (e) {
      if (my === seq.current) setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (my === seq.current) { setPeople(readPeopleData(store)); setNow(new Date()); setLoading(false); }
    }
  }, [familyId, fromISO, toISO, store]);

  useEffect(() => {
    if (!active) return;
    setLoading(true);
    void reload();
    let t: ReturnType<typeof setTimeout> | null = null;
    const soon = () => { if (t) clearTimeout(t); t = setTimeout(() => { void reload(); }, 400); };
    const unsub = backend.subscribeMonitor(familyId, soon);
    const iv = setInterval(soon, 60_000);
    const onData = () => soon();
    window.addEventListener('bh:data', onData);
    return () => { unsub(); clearInterval(iv); if (t) clearTimeout(t); window.removeEventListener('bh:data', onData); };
  }, [active, familyId, reload]);

  return { snap, people, loading, error, now, reload };
}
